import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, Minimize2, ArrowRight, ChevronUp } from "lucide-react";
import ComboIcon from "./ComboIcon";
import { IngredientTag, PremiumBadge } from "./IngredientCard";

const Bar = ({ ing, combo }) => (
    <motion.div
        layout
        layoutId={`ing-${ing.id || ing.name}`}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-center justify-between gap-4 border border-white/10 bg-bg1 px-4 py-3"
    >
        <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0">
                <ComboIcon icon={combo?.icon} url={combo?.icon_url} color={combo?.color} className="w-4 h-4" strokeWidth={3} />
            </span>
            <span className="text-sm text-white truncate">{ing.name}</span>
            {ing.premium && <span className="hidden sm:inline text-[9px] uppercase tracking-[0.18em] text-gold">Patented</span>}
        </div>
        <span className="mono text-xs text-gold whitespace-nowrap">{ing.quantity} {ing.unit}</span>
    </motion.div>
);

const DetailCard = ({ ing, combo }) => (
    <motion.div
        layout
        data-testid={`detail-card-${ing.id || ing.name}`}
        layoutId={`ing-${ing.id || ing.name}`}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className={`border p-5 transition-colors ${ing.premium
            ? "border-gold/45 bg-gradient-to-br from-gold/[0.12] to-transparent hover:border-gold/70"
            : "border-white/10 bg-bg1 hover:border-white/25"}`}
    >
        {ing.premium && <div className="mb-3"><PremiumBadge label={ing.badge_label || "Patented Premium Active"} /></div>}
        <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-white font-semibold">{ing.name}</div>
            <div className="mono text-xs text-gold whitespace-nowrap">{ing.quantity} {ing.unit}</div>
        </div>
        {ing.botanical_name && <div className="mt-1 text-xs italic text-zinc-500">{ing.botanical_name}</div>}
        {ing.description && <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{ing.description}</p>}
        <div className="mt-4 flex items-center justify-between gap-3">
            <IngredientTag combo={combo} />
            <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">per tablet</span>
        </div>
    </motion.div>
);

export const FormulationExplorer = ({ sections = {}, combos = [], byCombo = {}, comboMap = {},
    ingredients = [], basisLabel = "per tablet" }) => {
    const [openIds, setOpenIds] = useState([]);
    // Ingredients that belong to no live combo still have to be shown — as one flat formula group.
    const loose = ingredients.filter((i) => !i.combo_id || !comboMap[i.combo_id]);
    const rows = { ...byCombo };
    const groups = combos.filter((c) => (byCombo[c.id] || []).length);
    if (loose.length) {
        groups.push({ id: "__all", name: sections.detail_group_title || "The full formula" });
        rows.__all = loose;
    }
    const rowsFor = (id) => rows[id] || [];
    if (!groups.length) return null;

    const allOpen = openIds.length === groups.length;
    const toggleAll = () => setOpenIds(allOpen ? [] : groups.map((c) => c.id));
    const toggle = (id) => setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 mt-12 md:mt-16" data-testid="formulation-explorer">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div className="max-w-2xl">
                    <div className="eyebrow mb-3">Full Composition</div>
                    <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tight">
                        {sections.detail_title || "Explore the Formulation"}
                    </h2>
                    {sections.detail_subtitle && <p className="mt-5 text-zinc-400">{sections.detail_subtitle}</p>}
                </div>
                <button
                    onClick={toggleAll}
                    data-testid="explore-formulation-button"
                    className="btn-pill btn-primary inline-flex items-center gap-2 shrink-0"
                >
                    {allOpen ? <><Minimize2 className="w-4 h-4" /> Collapse All</> : <><Maximize2 className="w-4 h-4" /> Explore Full Formulation</>}
                </button>
            </div>

            <div className="mt-10 space-y-10">
                {groups.map((combo) => {
                    const rows = rowsFor(combo.id);
                    if (!rows.length) return null;
                    const open = openIds.includes(combo.id);
                    const accent = combo.color || "#7CB342";
                    return (
                        <div key={combo.id} data-testid={`explorer-combo-${combo.id}`}>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
                                {combo.id !== "__all" && (
                                    <span className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                                        style={{ background: `${accent}1f`, border: `1px solid ${accent}55` }}>
                                        <ComboIcon icon={combo.icon} url={combo.icon_url} color={accent} className="w-5 h-5" />
                                    </span>
                                )}
                                <h3 className="font-display text-xl uppercase tracking-tight">{combo.name}</h3>
                                <span className="mono text-[9px] tracking-[0.2em] text-zinc-600 hidden sm:inline">
                                    QUANTITY {basisLabel.toUpperCase()}
                                </span>
                                <button
                                    onClick={() => toggle(combo.id)}
                                    data-testid={`explorer-toggle-${combo.id}`}
                                    className="ml-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold hover:text-white transition-colors"
                                    style={{ color: accent }}
                                >
                                    {open ? <>Collapse <ChevronUp className="w-3.5 h-3.5" /></> : <>Explore Full Formula <ArrowRight className="w-3.5 h-3.5" /></>}
                                </button>
                            </div>
                            <AnimatePresence initial={false} mode="popLayout">
                                {open ? (
                                    <motion.div key="cards" layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {rows.map((ing) => <DetailCard key={ing.id || ing.name} ing={ing} combo={comboMap[ing.combo_id]} />)}
                                    </motion.div>
                                ) : (
                                    <motion.div key="bars" layout className="space-y-2 max-w-2xl">
                                        {rows.map((ing) => <Bar key={ing.id || ing.name} ing={ing} combo={comboMap[ing.combo_id]} />)}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default FormulationExplorer;
